/**
 * Agora Provider Implementation.
 *
 * This provider uses Agora's H.264 SEI approach
 * to transport animation data.
 *
 * Key differences from LiveKit:
 * - Uses native SEI events instead of RTCRtpScriptTransform
 * - No ALR (Application-Level Redundancy) needed - Agora handles reliability
 * - Simpler data extraction - uses SEI header parsing
 *
 * @packageDocumentation
 */

import { BaseProvider } from '../base/BaseProvider';
import type {
  RTCConnectionConfig,
  AgoraConnectionConfig,
  TransportStats,
} from '../../types';
import { isAgoraConfig, ConnectionState } from '../../types';
import type {
  AnimationTrackCallbacks,
  AudioTrackCallbacks,
} from '../../core/types';
import { SEIExtractor } from './SEIExtractor';
import { logger } from '../../utils';
import type {
  AgoraClient,
  AgoraLocalAudioTrack,
  AgoraRemoteAudioTrack,
  AgoraRemoteVideoTrack,
  AgoraUID,
} from './types';

/**
 * Agora Provider options.
 * @internal Reserved for future use
 */
export type AgoraProviderOptions = Record<string, never>;

/**
 * Remote user info with tracks
 * @internal
 */
interface RemoteUserInfo {
  videoTrack?: AgoraRemoteVideoTrack;
  audioTrack?: AgoraRemoteAudioTrack;
}

/**
 * Agora Provider.
 *
 * Implements RTCProvider interface for Agora platform.
 * Uses native SEI events to receive animation data from H.264 video tracks.
 *
 * @example
 * ```typescript
 * import { AvatarPlayer } from '@spatius/avatarkit-rtc';
 * import { AgoraProvider } from '@spatius/avatarkit-rtc/providers/agora';
 *
 * const provider = new AgoraProvider();
 * const player = new AvatarPlayer(provider, renderer);
 *
 * await player.connect({
 *   appId: 'your-agora-app-id',
 *   channel: 'your-channel',
 *   token: 'your-token',
 * });
 * ```
 */
export class AgoraProvider extends BaseProvider {
  /** Provider name identifier */
  readonly name = 'agora';

  /** @internal */
  private client: AgoraClient | null = null;
  /** @internal Dynamic SDK - type is any due to dynamic import */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private agoraSDK: any = null;

  /**
   * True when the client was injected by the host app (route B / external
   * client). In this mode we only borrow the client: we never call
   * `createClient`, `join`, or `leave` — the host owns the full RTC lifecycle.
   * We only attach our SEI listeners to extract avatar animation payloads.
   * @internal
   */
  private isExternalClient = false;
  /**
   * The `user-published` handler we register on a host-owned client, saved so
   * `detachExternalClient` can `off` exactly our listener without disturbing
   * the host's own `user-published` handlers.
   * @internal
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private externalUserPublishedHandler:
    | ((user: any, mediaType: 'audio' | 'video') => void)
    | null = null;

  // Animation track subscription (stored for cleanup, actual callbacks handled by SEIExtractor)
  /** @internal */
  private _animationCallbacks: AnimationTrackCallbacks | null = null;
  /** @internal */
  private seiExtractor: SEIExtractor | null = null;
  /** @internal */
  private remoteUsers: Map<AgoraUID, RemoteUserInfo> = new Map();
  /** @internal */
  private videoContainers: Map<AgoraUID, HTMLDivElement> = new Map();

  // Audio track subscription
  /** @internal */
  private audioCallbacks: AudioTrackCallbacks | null = null;
  /** @internal */
  private localAudioTrack: AgoraLocalAudioTrack | null = null;
  /** @internal */
  private remoteAudioTracks: Map<AgoraUID, AgoraRemoteAudioTrack> = new Map();

  // Debug mode
  /** @internal */
  private debugLogging = false;

  constructor(_options: AgoraProviderOptions = {}) {
    super();
  }

  /**
   * Enable or disable debug logging.
   * @param enabled - Whether to enable debug logging
   */
  setDebugLogging(enabled: boolean): void {
    this.debugLogging = enabled;
    if (this.seiExtractor) {
      this.seiExtractor.setDebugLogging(enabled);
    }
  }

  /**
   * Load Agora SDK dynamically.
   * @internal
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async loadSDK(): Promise<any> {
    if (this.agoraSDK) {
      return this.agoraSDK;
    }

    try {
      // Direct dynamic import - Vite will handle this properly with optimizeDeps
      // Using 'any' type due to dynamic import type inference limitations
      const sdk = await import('agora-rtc-sdk-ng');
      // Agora SDK exports default as the main AgoraRTC object
      this.agoraSDK = sdk.default ?? sdk;
      return this.agoraSDK;
    } catch (error) {
      logger.error('Agora', 'Failed to load SDK:', error);
      throw new Error(
        '❌ Failed to load agora-rtc-sdk-ng.\n' +
          'Please ensure it is installed: pnpm add agora-rtc-sdk-ng',
      );
    }
  }

  async connect(config: RTCConnectionConfig): Promise<void> {
    if (!isAgoraConfig(config)) {
      throw new Error(
        'AgoraProvider requires appId and channel in connection config',
      );
    }

    const agoraConfig: AgoraConnectionConfig = config;

    const AgoraRTC = await this.loadSDK();

    // Enable SEI reception (required for animation data)
    AgoraRTC.setParameter('ENABLE_VIDEO_SEI', true);
    AgoraRTC.setParameter('ENABLE_FULL_LINK_AV_SYNC', true);

    this.client = AgoraRTC.createClient({
      mode: 'rtc',
      codec: 'h264', // Required for SEI support
    });

    this.setConnectionState('connecting');

    // Setup event listeners
    this.setupEventListeners();

    try {
      await this.client.join(
        agoraConfig.appId,
        agoraConfig.channel,
        agoraConfig.token || null,
        agoraConfig.uid ?? null,
      );

      this.setConnectionState('connected');
      this.emit('connected');
    } catch (error) {
      this.setConnectionState('failed');
      this.emit('error', error as Error);
      throw error;
    }
  }

  /**
   * Setup Agora client event listeners
   * @internal
   */
  private setupEventListeners(): void {
    // Connection state changed
    this.client.on(
      'connection-state-change',
      (curState: string, _revState: string) => {
        const mapConnectionState = (state: string): ConnectionState => {
          switch (state) {
            case 'DISCONNECTED':
            case 'DISCONNECTING':
              return ConnectionState.Disconnected;
            case 'CONNECTING':
              return ConnectionState.Connecting;
            case 'CONNECTED':
              return ConnectionState.Connected;
            case 'RECONNECTING':
              return ConnectionState.Reconnecting;
            default:
              return ConnectionState.Failed;
          }
        };

        this.setConnectionState(mapConnectionState(curState));

        if (curState === 'CONNECTED') {
          this.emit('connected');
        } else if (curState === 'DISCONNECTED') {
          this.cleanup();
          this.emit('disconnected');
        }
      },
    );

    // User published (remote user joined and published tracks)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.client.on(
      'user-published',
      async (user: any, mediaType: 'audio' | 'video') => {
        if (this.debugLogging) {
          logger.info(
            'Agora',
            `User published: ${user.uid}, mediaType=${mediaType}`,
          );
        }

        try {
          // Subscribe to the track
          await this.client.subscribe(user, mediaType);

          if (mediaType === 'video') {
            if (user.videoTrack) {
              this.handleVideoTrack(user, user.videoTrack);
            } else {
              logger.warn(
                'Agora',
                `Video track is null after subscribe for user ${user.uid}`,
              );
            }
          } else if (mediaType === 'audio') {
            if (user.audioTrack) {
              this.handleAudioTrack(user, user.audioTrack);
            } else {
              logger.warn(
                'Agora',
                `Audio track is null after subscribe for user ${user.uid}`,
              );
            }
          }
        } catch (error) {
          logger.error(
            'Agora',
            `Failed to subscribe to ${mediaType} from ${user.uid}:`,
            error,
          );
          this.emit('error', error as Error);
        }
      },
    );

    // User unpublished (remote user stopped publishing)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.client.on(
      'user-unpublished',
      (user: any, mediaType: 'audio' | 'video') => {
        if (this.debugLogging) {
          logger.info(
            'Agora',
            `User unpublished: ${user.uid}, mediaType=${mediaType}`,
          );
        }

        if (mediaType === 'video') {
          this.removeVideoContainer(user.uid);
          this.remoteUsers.delete(user.uid);
        } else if (mediaType === 'audio') {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const track = this.remoteAudioTracks.get(user.uid) as any;
          if (track) {
            track.stop();
            this.remoteAudioTracks.delete(user.uid);
          }

          if (this.audioCallbacks) {
            this.audioCallbacks.onAudioLost?.(user);
          }
        }
      },
    );

    // User joined
    this.client.on('user-joined', (user: any) => {
      logger.info('Agora', `User joined: ${user.uid}`);
    });

    // User left
    this.client.on('user-left', (user: any, reason: string) => {
      logger.info('Agora', `User left: ${user.uid}, reason: ${reason}`);
      this.remoteUsers.delete(user.uid);
      const audioTrack = this.remoteAudioTracks.get(user.uid);
      if (audioTrack) {
        audioTrack.stop();
        this.remoteAudioTracks.delete(user.uid);
      }
    });

    // Error
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.client.on('exception', (error: any) => {
      logger.error('Agora', 'Exception:', error);
      this.emit('error', new Error(error.msg || String(error)));
    });
  }

  /**
   * Handle video track from remote user.
   * Sets up SEI event listeners and plays to hidden container.
   * @internal
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleVideoTrack(user: any, track: any): void {
    if (this.debugLogging) {
      logger.info('Agora', `Handling video track from ${user.uid}`);
    }

    // Listen for SEI data (animation frames)
    // Support both event names for SDK compatibility
    const seiHandler = (seiData: Uint8Array) => {
      if (this.seiExtractor) {
        this.seiExtractor.handleSEIData(seiData);
      }
    };

    // Try primary event name
    track.on('sei-received', seiHandler);
    // Also try alternative event name used in some SDK versions
    track.on('video-sei-received', seiHandler);

    // Play video to hidden element to ensure data flows
    // This is required for SEI events to be received
    const container = document.createElement('div');
    container.style.display = 'none';
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.id = `agora-video-${user.uid}`;
    document.body.appendChild(container);
    this.videoContainers.set(user.uid, container);
    track.play(container);

    if (this.debugLogging) {
      logger.info(
        'Agora',
        `Video track playing, SEI listeners attached for user ${user.uid}`,
      );
    }

    // Store track reference
    const existingUser = this.remoteUsers.get(user.uid);
    if (existingUser) {
      existingUser.videoTrack = track;
    } else {
      this.remoteUsers.set(user.uid, { videoTrack: track });
    }
  }

  /**
   * Handle audio track from remote user.
   * @internal
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleAudioTrack(user: any, track: any): void {
    if (this.debugLogging) {
      logger.info('Agora', `Handling audio track from ${user.uid}`);
    }

    // Play audio
    track.play();
    this.remoteAudioTracks.set(user.uid, track);

    if (this.debugLogging) {
      logger.info('Agora', `Audio track playing for user ${user.uid}`);
    }

    if (this.audioCallbacks) {
      this.audioCallbacks.onAudioReceived?.(user);
    }
  }

  /**
   * Remove video container for a user.
   * @internal
   */
  private removeVideoContainer(uid: number): void {
    const container = this.videoContainers.get(uid);
    if (container) {
      container.remove();
      this.videoContainers.delete(uid);
    }
  }

  async disconnect(): Promise<void> {
    // In external-client mode the host owns the RTC lifecycle: never leave the
    // channel or drop the host's client. Route to detach instead.
    if (this.isExternalClient) {
      this.detachExternalClient();
      return;
    }
    this.cleanup();
    if (this.client) {
      await this.client.leave();
      this.client = null;
    }
    this.setConnectionState('disconnected');
    this.emit('disconnected');
  }

  /**
   * Attach to a host-owned Agora client (route B / external client) instead of
   * creating one.
   *
   * In this mode the SDK does **not** own the RTC lifecycle: the host app is
   * responsible for creating the client, subscribing to encoded video, and
   * joining the channel. The SDK only attaches its SEI listeners to extract
   * avatar animation payloads and render them.
   *
   * Must be called **before** the host joins the channel: `ENABLE_VIDEO_SEI`
   * is a global Agora parameter that only takes effect for subsequent joins,
   * and the SEI listeners must be attached before the first frames arrive.
   *
   * The host must NOT call {@link connect} on this provider in external-client
   * mode — the two paths are mutually exclusive. The host's client must use the
   * `h264` codec (SEI is only delivered over H.264).
   *
   * @param client - The host-owned `IAgoraRTCClient` to borrow.
   * @throws Error if a client is already set.
   */
  async attachExternalClient(client: AgoraClient): Promise<void> {
    if (this.client) {
      throw new Error(
        'Client already set; call disconnect()/detachExternalClient() first',
      );
    }

    // Avatar SEI delivery requires this global parameter. The host owns the
    // client, but this is a hard requirement for the avatar to render, so we
    // set it here (before the host's join) rather than silently black-screening
    // when the host forgets. It's a global static on AgoraRTC — idempotent to
    // re-set — and only affects joins that happen after this call, which is why
    // attach must precede the host's join().
    const AgoraRTC = await this.loadSDK();
    AgoraRTC.setParameter('ENABLE_VIDEO_SEI', true);
    AgoraRTC.setParameter('ENABLE_FULL_LINK_AV_SYNC', true);

    this.client = client;
    this.isExternalClient = true;

    // Attach only our SEI-extraction listeners. We deliberately do NOT hook the
    // client's connection-state / user-joined / user-left / exception events:
    // the host owns connectivity and those callbacks. Our channel is SEI only,
    // exactly like iOS/Android where the SDK's encoded-frame observer runs
    // independently of the host's connection delegate.
    this.attachSEIListeners(client);

    // The host drives connectivity; from the SDK's point of view the data path
    // is ready as soon as the SEI listeners are attached.
    this.setConnectionState('connected');
  }

  /**
   * Detach from a host-owned client attached via {@link attachExternalClient}:
   * remove our SEI listeners and release our reference, but never leave the
   * channel or drop the host's client — the host owns those.
   */
  detachExternalClient(): void {
    if (!this.isExternalClient) {
      return;
    }
    const client = this.client;
    // Remove only the `user-published` handler we added, leaving the host's own
    // handlers intact.
    if (client && this.externalUserPublishedHandler) {
      client.off('user-published', this.externalUserPublishedHandler);
    }
    this.externalUserPublishedHandler = null;

    // Remove track-level SEI listeners, hidden containers, and the extractor —
    // this part is identical to the managed cleanup path.
    for (const [_uid, user] of this.remoteUsers) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const videoTrack = user.videoTrack as any;
      if (videoTrack) {
        videoTrack.off('sei-received');
        videoTrack.off('video-sei-received');
      }
    }
    for (const [uid] of this.videoContainers) {
      this.removeVideoContainer(uid as number);
    }
    this.remoteUsers.clear();
    if (this.seiExtractor) {
      this.seiExtractor.dispose();
      this.seiExtractor = null;
    }

    this.client = null;
    this.isExternalClient = false;
    // Only update state — do NOT emit the `disconnected` business event. The
    // idle-return teardown is owned by AvatarPlayer.detach() (dispose +
    // startIdle), matching iOS/Android detachExternalEngine which likewise
    // don't fire the disconnected event. Emitting here would run
    // animationHandler.startIdle() right before detach() disposes it.
    this.setConnectionState('disconnected');
  }

  /**
   * Attach SEI-extraction listeners to a client: subscribe to encoded video as
   * remote users publish, and hook `sei-received` on each remote video track.
   * Used only by the external-client path — the managed path drives this
   * through its full {@link setupEventListeners}.
   * @internal
   */
  private attachSEIListeners(client: AgoraClient): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handler = async (user: any, mediaType: 'audio' | 'video') => {
      if (mediaType !== 'video') return;
      try {
        await client.subscribe(user, mediaType);
        if (user.videoTrack) {
          this.handleVideoTrack(user, user.videoTrack);
        }
      } catch (error) {
        logger.error(
          'Agora',
          `Route B: failed to subscribe to video from ${user.uid}:`,
          error,
        );
        this.emit('error', error as Error);
      }
    };
    this.externalUserPublishedHandler = handler;
    client.on('user-published', handler);

    // The host may have already subscribed to remote video before attach; hook
    // SEI on any tracks that are already present.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const remoteUsers = (client as any).remoteUsers as any[];
    if (Array.isArray(remoteUsers)) {
      for (const user of remoteUsers) {
        if (user.videoTrack) {
          this.handleVideoTrack(user, user.videoTrack);
        }
      }
    }
  }

  getConnectionState(): string {
    if (!this.client) {
      return 'disconnected';
    }
    return this.connectionState;
  }

  /** @internal */
  async subscribeAnimationTrack(
    callbacks: AnimationTrackCallbacks,
  ): Promise<void> {
    this._animationCallbacks = callbacks;

    // Create SEI extractor
    this.seiExtractor = new SEIExtractor();
    this.seiExtractor.setDebugLogging(this.debugLogging);
    this.seiExtractor.initialize(callbacks);

    // If already connected, check for existing remote video tracks
    if (this.client) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const remoteUsers = (this.client as any).remoteUsers;
      for (const user of remoteUsers) {
        if (user.videoTrack) {
          // Subscribe to SEI events with both event names for compatibility
          const seiHandler = (seiData: Uint8Array) => {
            if (this.seiExtractor) {
              this.seiExtractor.handleSEIData(seiData);
            }
          };
          user.videoTrack.on('sei-received', seiHandler);
          user.videoTrack.on('video-sei-received', seiHandler);
        }
      }
    }
  }

  /** @internal */
  async unsubscribeAnimationTrack(): Promise<void> {
    this._animationCallbacks = null;
    if (this.seiExtractor) {
      this.seiExtractor.dispose();
      this.seiExtractor = null;
    }

    // Remove SEI listeners from remote tracks
    for (const [_uid, user] of this.remoteUsers) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const videoTrack = user.videoTrack as any;
      if (videoTrack) {
        videoTrack.off('sei-received');
        videoTrack.off('video-sei-received');
      }
    }

    // Remove video containers
    for (const [uid] of this.videoContainers) {
      this.removeVideoContainer(uid as number);
    }

    this.remoteUsers.clear();
  }

  /**
   * Transport-level view of the inbound animation stream.
   *
   * Agora reports this through its own accessors rather than the raw
   * RTCStatsReport, so the fields differ from LiveKit's: the loss rate comes
   * ready-made over a recent window (`currentPacketLossRate`, ~400ms) instead
   * of being derived from cumulative counters, and freeze time has no LiveKit
   * counterpart. RTT is per-connection, not per-track.
   *
   * Stats only become meaningful a few seconds after subscribing; before that
   * the SDK returns zeros, which is why an all-zero read is reported as-is
   * rather than specially cased — a genuinely idle link looks the same.
   * @internal
   */
  async getTransportStats(): Promise<TransportStats | null> {
    if (!this.client) return null;

    const stats: TransportStats = {};

    try {
      // Keyed by uid; the animation stream is the one video track we
      // subscribed to, so take the first entry that has counters.
      const remote = this.client.getRemoteVideoStats();
      for (const uid of Object.keys(remote)) {
        const v = remote[uid];
        if (!v) continue;
        if (typeof v.receivePacketsLost === 'number') {
          stats.packetsLost = v.receivePacketsLost;
        }
        if (typeof v.receivePackets === 'number') {
          stats.packetsReceived = v.receivePackets;
        }
        if (typeof v.currentPacketLossRate === 'number') {
          stats.lossRatePct = v.currentPacketLossRate;
        }
        if (typeof v.totalFreezeTime === 'number') {
          stats.freezeTimeSec = v.totalFreezeTime;
        }
        if (stats.packetsLost !== undefined) break;
      }
    } catch {
      // Best-effort; a mid-teardown read is not worth failing the tick over.
    }

    try {
      const rtc = this.client.getRTCStats();
      if (rtc && typeof rtc.RTT === 'number' && rtc.RTT > 0) {
        stats.rttMs = rtc.RTT;
      }
    } catch {
      // As above.
    }

    return Object.keys(stats).length > 0 ? stats : null;
  }

  /** @internal */
  async subscribeAudioTrack(callbacks: AudioTrackCallbacks): Promise<void> {
    this.audioCallbacks = callbacks;
    // Audio tracks are automatically handled in user-published event
  }

  /** @internal */
  async unsubscribeAudioTrack(): Promise<void> {
    this.audioCallbacks = null;
    this.remoteAudioTracks.forEach((track) => {
      track.stop();
    });
    this.remoteAudioTracks.clear();
  }

  async publishAudioTrack(track?: MediaStreamTrack): Promise<void> {
    if (!this.client) {
      throw new Error('Not connected to channel');
    }

    const AgoraRTC = await this.loadSDK();

    if (track) {
      // Create local audio track from provided MediaStreamTrack
      this.localAudioTrack = AgoraRTC.createCustomAudioTrack({
        mediaStreamTrack: track,
      });
    } else {
      // Create local audio track from microphone
      this.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
        encoderConfig: 'music_standard',
        AEC: true,
        ANS: true,
        AGC: true,
      });
    }

    // Publish the track
    await this.client.publish(this.localAudioTrack);
  }

  async unpublishAudioTrack(): Promise<void> {
    if (!this.client || !this.localAudioTrack) {
      return;
    }

    await this.client.unpublish(this.localAudioTrack);
    this.localAudioTrack.close();
    this.localAudioTrack = null;
  }

  /**
   * Get the native Agora RTC Client instance.
   *
   * Allows advanced users to access Agora-specific features
   * not exposed through the unified API.
   *
   * @returns The Agora IAgoraRTCClient instance, or null if not connected
   *
   * @example
   * ```typescript
   * const client = provider.getNativeClient();
   * if (client) {
   *   // Access Agora-specific features
   *   console.log('Connection state:', client.connectionState);
   * }
   * ```
   */
  getNativeClient(): AgoraClient | null {
    return this.client;
  }

  /**
   * Cleanup resources
   * @internal
   */
  private cleanup(): void {
    // Cleanup remote audio tracks
    this.remoteAudioTracks.forEach((track) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (track as any).stop();
    });
    this.remoteAudioTracks.clear();

    // Cleanup remote video tracks and SEI listeners
    for (const [_uid, user] of this.remoteUsers) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const videoTrack = user.videoTrack as any;
      if (videoTrack) {
        videoTrack.off('sei-received');
        videoTrack.off('video-sei-received');
      }
    }
    this.remoteUsers.clear();

    // Cleanup video containers
    for (const [uid] of this.videoContainers) {
      this.removeVideoContainer(uid as number);
    }

    // Cleanup SEI extractor
    if (this.seiExtractor) {
      this.seiExtractor.dispose();
      this.seiExtractor = null;
    }

    // Cleanup local audio track
    if (this.localAudioTrack) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this.localAudioTrack as any).close();
      this.localAudioTrack = null;
    }
  }
}
